import { useState, useEffect } from "react";
import { Search, Plus, Edit2, Trash2, Loader2, X, CheckCircle, AlertCircle, UserIcon } from "lucide-react";
import { adminService, User } from "../../../services/adminService";

export function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [formData, setFormData] = useState({ name: "", email: "", password: "", role: "admin" });

  useEffect(() => { loadUsers(); }, [searchTerm]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const result = await adminService.listUsers(searchTerm);
      setUsers(result.data);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await adminService.updateUser(editingUser.id, formData);
        setMessage({ type: "success", text: "Usuário atualizado com sucesso!" });
      } else {
        await adminService.createUser(formData as any);
        setMessage({ type: "success", text: "Usuário criado com sucesso!" });
      }
      closeModal();
      loadUsers();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao salvar usuário" });
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({ name: user.name, email: user.email, password: "", role: user.role });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Deseja desativar este usuário?")) return;
    try {
      await adminService.deleteUser(id);
      setMessage({ type: "success", text: "Usuário desativado!" });
      loadUsers();
    } catch (error: any) { 
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao desativar usuário" }); 
    }
  };

  const openNewModal = () => {
    setEditingUser(null);
    setFormData({ name: "", email: "", password: "", role: "admin" });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingUser(null); };

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
          {message.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input type="text" placeholder="Buscar usuários..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-input rounded-lg bg-card text-foreground placeholder:text-muted-foreground" />
        </div>
        <button onClick={openNewModal} className="flex items-center gap-2 px-4 py-2 btn btn-primary">
          <Plus className="w-4 h-4" /> Novo Usuário
        </button>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">Nenhum usuário encontrado</div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Perfil</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-muted/50">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <UserIcon className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium text-foreground">{user.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-muted-foreground">{user.email}</td>
                  <td className="px-4 py-4">
                    <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-700 dark:text-blue-400">
                      {user.role === 'admin' ? 'Administrador' : user.role}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${user.active ? "bg-green-500/20 text-green-700 dark:text-green-400" : "bg-red-500/20 text-red-700 dark:text-red-400"}`}>
                      {user.active ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEdit(user)} className="p-2 text-blue-600 hover:bg-blue-500/10 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(user.id)} className="p-2 text-red-600 hover:bg-red-500/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">{editingUser ? "Editar Usuário" : "Novo Usuário"}</h3>
              <button onClick={closeModal}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Nome *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} required 
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} required 
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Senha {editingUser ? "(deixe vazio para manter)" : "*"}</label>
                <input type="password" value={formData.password} onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))} required={!editingUser} 
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Perfil</label>
                <select value={formData.role} onChange={(e) => setFormData(p => ({ ...p, role: e.target.value }))} 
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground">
                  <option value="admin">Administrador</option>
                  <option value="gerente">Gerente</option>
                  <option value="operador">Operador</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeModal} className="flex-1 py-2 btn btn-outline">Cancelar</button>
                <button type="submit" className="flex-1 py-2 btn btn-primary">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
